import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X, Shield, Building2, UserX, Download, Archive } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const BulkActionsBar = ({ selectedItems, onClearSelection, onSuccess, type = "users", roles, subsidiaries }) => {
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSubsidiary, setSelectedSubsidiary] = useState("");

  const handleBulkAssignRole = async () => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bulk/users/assign-role`, {
        user_ids: selectedItems,
        role_id: selectedRole,
        scope_type: "company"
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn("Bulk assign errors:", response.data.errors);
      }
      
      setSelectedRole("");
      if (onSuccess) onSuccess();
      if (onClearSelection) onClearSelection();
    } catch (error) {
      console.error("Bulk assign role error:", error);
      toast.error("Failed to assign role to users");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAssignSubsidiary = async () => {
    if (!selectedSubsidiary) {
      toast.error("Please select a subsidiary");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bulk/users/assign-subsidiary`, {
        user_ids: selectedItems,
        subsidiary_id: selectedSubsidiary
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn("Bulk assign errors:", response.data.errors);
      }
      
      setSelectedSubsidiary("");
      if (onSuccess) onSuccess();
      if (onClearSelection) onClearSelection();
    } catch (error) {
      console.error("Bulk assign subsidiary error:", error);
      toast.error("Failed to assign users to subsidiary");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Are you sure you want to deactivate ${selectedItems.length} user(s)?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bulk/users/deactivate`, {
        user_ids: selectedItems
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn("Bulk deactivate errors:", response.data.errors);
      }
      
      if (onSuccess) onSuccess();
      if (onClearSelection) onClearSelection();
    } catch (error) {
      console.error("Bulk deactivate error:", error);
      toast.error("Failed to deactivate users");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (!window.confirm(`Are you sure you want to archive ${selectedItems.length} subsidiar${selectedItems.length === 1 ? 'y' : 'ies'}?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bulk/subsidiaries/archive`, {
        subsidiary_ids: selectedItems
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn("Bulk archive errors:", response.data.errors);
      }
      
      if (onSuccess) onSuccess();
      if (onClearSelection) onClearSelection();
    } catch (error) {
      console.error("Bulk archive error:", error);
      toast.error("Failed to archive subsidiaries");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Create CSV content
    let csvContent = "";
    
    if (type === "users") {
      csvContent = "data:text/csv;charset=utf-8,User IDs\n";
      selectedItems.forEach(id => {
        csvContent += id + "\n";
      });
    } else {
      csvContent = "data:text/csv;charset=utf-8,Subsidiary IDs\n";
      selectedItems.forEach(id => {
        csvContent += id + "\n";
      });
    }
    
    // Trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${selectedItems.length} ${type} exported to CSV`);
  };

  if (selectedItems.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 bg-slate-900 text-white rounded-lg shadow-2xl p-4 flex items-center gap-4 animate-in slide-in-from-bottom">
      <div className="flex items-center gap-2">
        <span className="font-medium">{selectedItems.length} selected</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClearSelection}
          className="text-white hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="h-6 w-px bg-slate-600"></div>

      {type === "users" ? (
        <>
          {/* Assign Role */}
          <div className="flex items-center gap-2">
            <Select value={selectedRole} onValueChange={setSelectedRole} disabled={loading}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select role..." />
              </SelectTrigger>
              <SelectContent>
                {roles?.filter(r => r.role_scope !== "platform").map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleBulkAssignRole}
              disabled={!selectedRole || loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Shield className="h-4 w-4 mr-1" />
              Assign Role
            </Button>
          </div>

          {/* Assign to Subsidiary */}
          <div className="flex items-center gap-2">
            <Select value={selectedSubsidiary} onValueChange={setSelectedSubsidiary} disabled={loading}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select subsidiary..." />
              </SelectTrigger>
              <SelectContent>
                {subsidiaries?.map((sub) => (
                  <SelectItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleBulkAssignSubsidiary}
              disabled={!selectedSubsidiary || loading}
              className="bg-green-600 hover:bg-green-700"
            >
              <Building2 className="h-4 w-4 mr-1" />
              Assign Subsidiary
            </Button>
          </div>

          {/* Deactivate */}
          <Button
            size="sm"
            variant="destructive"
            onClick={handleBulkDeactivate}
            disabled={loading}
          >
            <UserX className="h-4 w-4 mr-1" />
            Deactivate
          </Button>
        </>
      ) : (
        <>
          {/* Archive Subsidiaries */}
          <Button
            size="sm"
            variant="destructive"
            onClick={handleBulkArchive}
            disabled={loading}
          >
            <Archive className="h-4 w-4 mr-1" />
            Archive
          </Button>
        </>
      )}

      {/* Export */}
      <Button
        size="sm"
        variant="ghost"
        onClick={handleExport}
        className="text-white hover:bg-slate-800"
      >
        <Download className="h-4 w-4 mr-1" />
        Export CSV
      </Button>
    </div>
  );
};

export default BulkActionsBar;
